import React from 'react'
import { FormTextField, useWatch, useZionFormContext } from '@fs/zion-form'
import { Cell, LabelSpacer, Row } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import {
  useSlsStateContext,
  getDisplayLabelForFieldType,
  getSlsTypeByValue,
  useSlsHighlightsContext,
} from '@fs/zion-sls'
import useNamesExperienceOptIn from '../../../../hooks/useNamesExperienceOptIn'
import { useCollectionContextProvider } from '../../../../providers/collectionProvider/CollectionProvider'
import { FIELDS } from '../../../../providers/collectionProvider/collectionConstants'
import HighlightButton from '../../panels/shared/HighlightButton'
import LinkNamesButton from '../../panels/shared/LinkNamesButton'
import { handleInputFocus, handleInputBlur, prepareFieldForHighlights } from '../../shared/utils/highlightUtils'

// The collection restriction that locks each free-text detail
const TEXT_DETAIL_RESTRICTIONS = { RACE_OR_COLOR: FIELDS.RACE, AGE: FIELDS.AGE }

/**
 * A free-text person detail (Race or Age), laid out like the Sex row.
 *
 * @param {Object} props
 * @param {'RACE_OR_COLOR'|'AGE'} props.fieldType - The SLS field type the row edits.
 * @param {string} props.testIdPrefix - Prefix for the row's test ids and link names.
 * @param {Object} [props.person] - The SLS person, needed to link the field.
 * @param {string} [props.splitScreenImageArk] - The split-screen image to highlight on.
 * @param {boolean} props.isNewPerson - Whether the person isn't saved in SLS yet.
 * @param {Function} props.onHighlightSave - Handles what the highlight panel hands back.
 * @param {Function} props.onLinkNamesSave - Handles what the link names panel hands back.
 * @returns {JSX.Element} The row, plus the link names button for a linked field.
 */
const TextDetailField = ({
  fieldType,
  testIdPrefix,
  person,
  splitScreenImageArk,
  isNewPerson,
  onHighlightSave,
  onLinkNamesSave,
}) => {
  const [t] = useTranslation()
  const {
    formState: { defaultValues },
    getValues,
  } = useZionFormContext()
  const field = useWatch({ name: fieldType })
  const h = useSlsHighlightsContext()
  const { types: slsTypes, images } = useSlsStateContext()
  const { restrictedFields } = useCollectionContextProvider()
  const { isOptedIn } = useNamesExperienceOptIn()

  // Both the collection and SLS can lock the field
  const isRestricted = restrictedFields?.includes(TEXT_DETAIL_RESTRICTIONS[fieldType])
  const isEditable = !isRestricted && (getSlsTypeByValue(fieldType, 'fields', slsTypes)?.editable ?? true)

  return (
    <>
      <Row gutters="xxs">
        <FormTextField
          name={`${fieldType}.value`}
          label={getDisplayLabelForFieldType(fieldType, slsTypes)}
          disabled={!isEditable}
          onFocus={() => handleInputFocus(`${fieldType}.id`, getValues, h)}
          onBlur={() => handleInputBlur(h)}
          data-testid={`${testIdPrefix}-field`}
          warningValidate={(value) =>
            field?.shared && !isNewPerson && value !== (defaultValues?.[fieldType]?.value || '')
              ? t(
                  'essentialInformationForm.names.shared-field-change-warning',
                  'Changes will be made on all who are linked.'
                )
              : null
          }
          dense
          growX
        />
        <Cell>
          <LabelSpacer />
          <HighlightButton
            field={prepareFieldForHighlights(getValues, fieldType, fieldType, slsTypes, images)}
            splitScreenImageArk={splitScreenImageArk}
            buttonProps={{ 'data-testid': `${testIdPrefix}-highlight-button` }}
            linkName={`essential-information-${testIdPrefix}-highlight`}
            disabled={!isEditable}
            onSave={onHighlightSave}
            saveToSls={!isNewPerson && !getValues(`${fieldType}.added`)}
            growX
            iconOnly
          />
        </Cell>
      </Row>
      {isOptedIn && person && field?.shared && !isNewPerson && (
        <LinkNamesButton
          person={person}
          field={field}
          onSave={onLinkNamesSave}
          linkedPersonIds={field?.linkedPersonIds || []}
        />
      )}
    </>
  )
}

export default TextDetailField
